import type {ComboboxProps} from '@/components/ui/combobox';
import {Combobox} from '@/components/ui/combobox';
import {graphqlClient} from '@/services/graphqlClient';
import type {WarehouseItem} from '@/services/graphqlHooks';
import {useGetWarehouseItemsQuery} from '@/services/graphqlHooks';

export type WarehouseItemOption = WarehouseItem & {
  fallbackText: string;
  value: string;
  label: string;
  keywords: string[];
};

export type WarehouseItemInputProps = Omit<
  ComboboxProps<WarehouseItemOption>,
  'items'
> & {
  value: string;
  onChange: (value: string) => void;
  warehouseId?: string;
};

const WarehouseItemInput = ({
  value,
  onChange,
  warehouseId = '',
  ...props
}: WarehouseItemInputProps) => {
  const {data} = useGetWarehouseItemsQuery(
    graphqlClient,
    {
      warehouseId,
    },
    {
      enabled: !!warehouseId,
      staleTime: Infinity,
    }
  );

  const warehouseItems = data?.getWarehouseItems?.items || [];

  const warehouseItemsOptions = warehouseItems.map(items => ({
    keywords: [items.name, items.id],
    label: items.name,
    value: items.id,
    ...items,
  })) as WarehouseItemOption[];

  const selectedItem =
    warehouseItemsOptions.find(options => options.value === value) || null;

  return (
    <Combobox
      items={warehouseItemsOptions}
      itemToString={option => option?.label || ''}
      selectedItem={selectedItem}
      onSelectedItemChange={({selectedItem}) => {
        onChange(selectedItem?.value || '');
      }}
      {...props}
    />
  );
};

export default WarehouseItemInput;
